import React from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, Search, X } from 'lucide-react';

/**
 * 通用分页条 + 搜索框（前端本地过滤分页共用组件）
 *
 * 用法：
 *   const [page, setPage] = useState(1);
 *   const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE); // 默认 10
 *   <SearchBox value={kw} onChange={setKw} placeholder="搜索..." />
 *   <PaginationBar total={list.length} page={page} pageSize={pageSize}
 *                  onPageChange={setPage} onPageSizeChange={setPageSize} />
 *
 * 备注（PG-001）：2026-11-04 为订单管理/卡密库存/商品列表/关键词管理四个页面
 * 统一加分页与搜索，默认每页 10 条，本组件只做展示，切片逻辑在各页面内完成。
 */

/** 默认每页条数：产品约定统一 10 */
export const DEFAULT_PAGE_SIZE = 10;
/** 每页条数可选项 */
export const PAGE_SIZE_OPTIONS = [10, 20, 50];

interface PaginationBarProps {
  /** 过滤后的总条数 */
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** 是否隐藏（如列表为空时外部自行控制） */
  className?: string;
}

/** 计算省略页码序列：1 … c-1 c c+1 … last */
const buildPageNumbers = (page: number, totalPages: number): (number | '...')[] => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const set = new Set<number>([1, totalPages, page]);
  if (page - 1 > 1) set.add(page - 1);
  if (page + 1 < totalPages) set.add(page + 1);
  if (page <= 3) { set.add(2); set.add(3); set.add(4); }
  if (page >= totalPages - 2) { set.add(totalPages - 1); set.add(totalPages - 2); set.add(totalPages - 3); }
  const sorted = Array.from(set).filter(n => n >= 1 && n <= totalPages).sort((a, b) => a - b);
  const out: (number | '...')[] = [];
  let prev = 0;
  for (const n of sorted) {
    if (n - prev > 1) out.push('...');
    out.push(n);
    prev = n;
  }
  return out;
};

export const PaginationBar: React.FC<PaginationBarProps> = ({
  total, page, pageSize, onPageChange, onPageSizeChange, className = '',
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // 数据变少后当前页可能越界，展示时钳制，避免停在空白页
  const safePage = Math.min(page, totalPages);

  return (
    <div className={`p-4 border-t border-gray-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white ${className}`}>
      <div className="text-sm text-gray-500 font-medium pl-2 flex items-center gap-2 flex-wrap">
        <span>共 <span className="text-gray-900 font-extrabold">{total}</span> 条</span>
        <span className="text-gray-300">·</span>
        <span>第 <span className="text-gray-900 font-extrabold">{safePage}</span> / {totalPages} 页</span>
      </div>
      <div className="flex items-center gap-1.5 pl-2 sm:pl-0 flex-wrap">
        {/* 每页条数切换 */}
        <div className="flex items-center gap-1.5 mr-1 text-xs text-gray-500 font-medium">
          <span>每页</span>
          <div className="relative">
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="appearance-none bg-gray-50 hover:bg-gray-100 border border-transparent rounded-lg pl-2.5 pr-7 py-1.5 text-xs font-bold text-gray-700 outline-none cursor-pointer transition-colors"
            >
              {PAGE_SIZE_OPTIONS.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
          <span>条</span>
        </div>
        {/* 上一页 */}
        <button
          disabled={safePage <= 1}
          onClick={() => onPageChange(safePage - 1)}
          className="flex items-center gap-1 px-3 py-2 rounded-xl bg-gray-50 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 text-xs font-bold transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          上一页
        </button>
        {/* 页码 */}
        {buildPageNumbers(safePage, totalPages).map((n, idx) =>
          n === '...' ? (
            <span key={`e-${idx}`} className="px-1 text-xs text-gray-400">…</span>
          ) : (
            <button
              key={n}
              onClick={() => onPageChange(n)}
              className={`min-w-[34px] px-2 py-2 rounded-xl text-xs font-bold transition-colors ${
                n === safePage
                  ? 'bg-gray-900 text-white shadow-md'
                  : 'bg-gray-50 hover:bg-gray-100 text-gray-600'
              }`}
            >
              {n}
            </button>
          )
        )}
        {/* 下一页 */}
        <button
          disabled={safePage >= totalPages}
          onClick={() => onPageChange(safePage + 1)}
          className="flex items-center gap-1 px-3 py-2 rounded-xl bg-gray-50 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 text-xs font-bold transition-colors"
        >
          下一页
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

interface SearchBoxProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}

/** 通用搜索输入框：输入即过滤（各页面自行联动 setPage(1)） */
export const SearchBox: React.FC<SearchBoxProps> = ({ value, onChange, placeholder = '搜索...', className = '' }) => (
  <div className={`relative group ${className}`}>
    <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#FFE815] transition-colors" />
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="ios-input pl-10 pr-9 py-2.5 rounded-xl w-full sm:w-60 bg-white border-none shadow-sm focus:ring-0 text-sm"
    />
    {value && (
      <button
        onClick={() => onChange('')}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-gray-300 hover:text-gray-500 transition-colors"
        title="清空"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    )}
  </div>
);
